import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import {
  ProcessPath,
  ProcessPathMetrics,
  Workstation,
  AssignmentAssociate,
} from '../models';
import WorkstationCard from './WorkstationCard';
import IndirectAssociateRow from './IndirectAssociateRow';

export interface ProcessPathSectionProps {
  processPath: ProcessPath;
  expanded: boolean;
  onToggleExpanded: (processPathId: string) => void;
  onAssociateRemove: (associateId: string, locationId: string) => void;
  onAddAssociate: (workstationId: string) => void;
}

/**
 * A single metric cell rendered inside the per-path metrics row. Kept as a
 * small component so the DOM is predictable for tests (label and value are
 * adjacent `Text` nodes) and so labels can be queried verbatim.
 */
const MetricCell: React.FC<{ label: string; value: number | string }> = ({
  label,
  value,
}) => (
  <Box className="assignments-process-path__metric" spacingInset="200">
    <Text type="b200">{label}</Text>
    <Text type="h100">{String(value)}</Text>
  </Box>
);

/**
 * Render the metrics row for a direct process path.
 *
 * Direct paths surface flow-oriented numbers (total flow, delta,
 * avg rate per hour). Optional fields fall back to a dash so the cell
 * still renders and tests can reliably locate the label.
 */
const DirectMetricsRow: React.FC<{ metrics: ProcessPathMetrics }> = ({
  metrics,
}) => (
  <Box
    className="assignments-process-path__metrics assignments-process-path__metrics--direct"
    spacingInset="200"
  >
    <MetricCell label="Total flow" value={metrics.totalFlow ?? '—'} />
    <MetricCell label="Actual" value={metrics.actual} />
    <MetricCell label="Assigned" value={metrics.assigned} />
    <MetricCell label="Delta" value={metrics.delta} />
    <MetricCell
      label="Avg rate per hour"
      value={metrics.avgRatePerHour ?? '—'}
    />
  </Box>
);

/**
 * Render the metrics row for an indirect process path.
 *
 * Indirect paths care about processed packages and scan rate rather than
 * total flow. Optional fields fall back to a dash for the same reason as
 * `DirectMetricsRow`.
 */
const IndirectMetricsRow: React.FC<{ metrics: ProcessPathMetrics }> = ({
  metrics,
}) => (
  <Box
    className="assignments-process-path__metrics assignments-process-path__metrics--indirect"
    spacingInset="200"
  >
    <MetricCell label="Actual" value={metrics.actual} />
    <MetricCell label="Assigned" value={metrics.assigned} />
    <MetricCell label="Delta" value={metrics.delta} />
    <MetricCell label="DR" value={metrics.dr ?? '—'} />
    <MetricCell label="Avg scan rate" value={metrics.avgScanRate ?? '—'} />
    <MetricCell label="Units per hour" value={metrics.unitsPerHour ?? '—'} />
    <MetricCell
      label="Processed packages"
      value={metrics.processedPackages ?? '—'}
    />
  </Box>
);

/**
 * Collapsible section representing a single process path.
 *
 * The header is a clickable area that toggles the section's expanded state
 * via `onToggleExpanded`. It displays the path name and a badge indicating
 * whether the path is Direct or Indirect. A separate "Show workstations"
 * Button is rendered alongside the header for controlling workstation
 * visibility independently of the collapse state.
 *
 * When expanded:
 *   - Direct paths render a per-path metrics row followed by one
 *     `WorkstationCard` per workstation.
 *   - Indirect paths render a per-path metrics row followed by a
 *     horizontal flex row of `IndirectAssociateRow` items (no workstation
 *     cards).
 *
 * The component is a thin renderer: all associate / workstation state is
 * passed in via the `processPath` prop, and mutations are reported up to
 * the parent via `onAssociateRemove` and `onAddAssociate`.
 */
const ProcessPathSection: React.FC<ProcessPathSectionProps> = ({
  processPath,
  expanded,
  onToggleExpanded,
  onAssociateRemove,
  onAddAssociate,
}) => {
  const isDirect = processPath.pathType === 'direct';
  const badgeLabel = isDirect ? 'Direct' : 'Indirect';

  const handleHeaderClick = () => {
    onToggleExpanded(processPath.processPathId);
  };

  // The "Show workstations" button sits in the header row alongside the
  // header toggle. Wiring its toggle semantics to parent state is a
  // follow-up; for now the button exists so tests can observe its
  // presence (Req 7.8) without it also triggering the header expand.
  const handleShowWorkstationsClick = () => {
    // Intentional no-op for now. A future task will flow this through to
    // a separate workstation-visibility toggle.
  };

  const chevronTokens = expanded
    ? chevronDownSmallTokens
    : chevronRightSmallTokens;

  // Indirect paths may omit `indirectAssociates` entirely; treat that case
  // as an empty list so the flex row still renders (just with no children).
  const indirectAssociates: AssignmentAssociate[] =
    processPath.indirectAssociates ?? [];

  return (
    <Box className="assignments-process-path" spacingInset="300">
      <Box className="assignments-process-path__header-row">
        {/*
          Native <button> gives us click + keyboard activation without
          depending on Meridian Box exposing onClick in its typings.
          The "Show workstations" Button is rendered as a sibling (not a
          child) so it doesn't end up nested inside another button.
        */}
        <button
          type="button"
          className="assignments-process-path__header"
          onClick={handleHeaderClick}
          aria-expanded={expanded}
        >
          <Icon tokens={chevronTokens} />
          <Text type="h200">{processPath.processPathName}</Text>
          <Tag type={isDirect ? 'neutral' : 'theme'}>{badgeLabel}</Tag>
        </button>
        <Box className="assignments-process-path__header-actions">
          <Button type="tertiary" onClick={handleShowWorkstationsClick}>
            Show workstations
          </Button>
        </Box>
      </Box>

      {expanded && (
        <Box className="assignments-process-path__body">
          {isDirect ? (
            <DirectMetricsRow metrics={processPath.metrics} />
          ) : (
            <IndirectMetricsRow metrics={processPath.metrics} />
          )}

          {isDirect ? (
            <Box className="assignments-process-path__workstations">
              {processPath.workstations.map((workstation: Workstation) => (
                <WorkstationCard
                  key={workstation.workstationId}
                  workstation={workstation}
                  onAssociateRemove={onAssociateRemove}
                  onAddAssociate={onAddAssociate}
                />
              ))}
            </Box>
          ) : (
            <Box className="assignments-process-path__indirect-associates">
              {indirectAssociates.map((associate) => (
                <IndirectAssociateRow
                  key={associate.associateId}
                  associate={associate}
                  processPathId={processPath.processPathId}
                  onRemove={onAssociateRemove}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProcessPathSection;
